import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { theme as T } from '../../theme';
import DateRangeCalendar from './DateRangeCalendar';
import { resolveTokenRange, customToken } from '../../utils/reportPeriod';

/** Fixed width of the desktop panel — also the width the edge test measures against. */
const DESKTOP_PANEL_WIDTH = 300;
/** Breathing room kept between the panel and the layout edge when it flips. */
const EDGE_MARGIN = 12;

export interface QuickSelectOption {
  /** Stored filter token, e.g. '7D' — unchanged from the pre-GLOBAL-DP vocabulary. */
  token: string;
  /** Rendered label, e.g. 'Last 7 days'. */
  label: string;
}

export interface DateRangePopoverProps {
  variant: 'desktop' | 'mobile';
  onClose: () => void;
  /** Currently applied range — pre-populates FROM/TO when the overlay opens. */
  activeStart: string;
  activeEnd: string;
  quickSelects: QuickSelectOption[];
  /** Quick-select chosen: applies immediately, no Apply needed. */
  onSelectToken: (token: string) => void;
  /** Apply pressed with an explicit FROM/TO pair. */
  onApplyRange: (token: string) => void;
  /** Latest selectable day; omit to allow future dates. */
  maxDate?: string;
  todayISO: string;
  storeTz: string;
}

/**
 * The date-picker overlay behind the unified date control (GLOBAL-DP-1).
 *
 * Structure, top to bottom: the existing Maghil range calendar (FROM/TO fields, month +
 * year selector, month navigation, day grid), then the per-report quick-select row, then
 * Cancel / Apply.
 *
 * The overlay is mounted only while open — that is what makes "navigate the visible month
 * back, press Cancel, reopen" land back on the month containing the active selection: the
 * calendar's own view state is discarded with the unmount rather than being reset by hand.
 *
 * Desktop renders an anchored popover; mobile renders a bottom sheet with a dimmed
 * backdrop. Behaviour is otherwise identical across the two.
 */
export default function DateRangePopover({
  variant,
  onClose,
  activeStart,
  activeEnd,
  quickSelects,
  onSelectToken,
  onApplyRange,
  maxDate,
  todayISO,
  storeTz,
}: DateRangePopoverProps) {
  const isMobile = variant === 'mobile';
  const panelRef = useRef<HTMLDivElement>(null);

  // Staged selection — the applied value only changes on Apply or a quick-select.
  const [fromDate, setFromDate] = useState(activeStart || todayISO);
  const [toDate, setToDate] = useState(activeEnd || activeStart || todayISO);

  // Dismiss on outside click / Escape. The mobile sheet uses its own backdrop element,
  // so the pointer listener is desktop-only.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    if (isMobile) return () => document.removeEventListener('keydown', onKeyDown);
    const onMouseDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isMobile, onClose]);

  /**
   * Which edge of the control the desktop panel hangs off.
   *
   * It anchors left by default, but the date control is the LAST TopBar item on every
   * report that passes neither `sourceFilters` nor `extraContent` (Product, EOD, Check-In,
   * Reservations, Customers, Events) — there the control sits flush against the layout's
   * right padding and a left-anchored 300px panel runs past it. That is not merely
   * off-screen: DesktopLayout's outer wrapper is `overflow: hidden`, so the overflow is
   * CLIPPED and the calendar's month nav plus the Cancel/Apply row are cut in half.
   *
   * Measured from the anchor rather than from the panel's own post-layout rect, so
   * flipping cannot feed back into the measurement and oscillate.
   */
  const [alignRight, setAlignRight] = useState(false);
  useLayoutEffect(() => {
    if (isMobile) return;
    const anchor = panelRef.current?.parentElement;
    if (!anchor) return;
    const measure = () => {
      const rect = anchor.getBoundingClientRect();
      const overflowsLeftAnchored = rect.left + DESKTOP_PANEL_WIDTH > window.innerWidth - EDGE_MARGIN;
      const fitsRightAnchored = rect.right - DESKTOP_PANEL_WIDTH >= EDGE_MARGIN;
      setAlignRight(overflowsLeftAnchored && fitsRightAnchored);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isMobile]);

  // A quick-select reads as active only when the applied range is exactly the range that
  // option resolves to. Comparing resolved ranges (not tokens) means a stepper that lands
  // back on, say, today still lights up "Today", while a hand-picked range lights nothing.
  const activeToken = useMemo(() => {
    for (const opt of quickSelects) {
      const r = resolveTokenRange(opt.token, storeTz);
      if (r && r.startDate === activeStart && r.endDate === activeEnd) return opt.token;
    }
    return null;
  }, [quickSelects, activeStart, activeEnd, storeTz]);

  const rangeInvalid = !fromDate || !toDate || fromDate > toDate;

  const calendar = (
    <DateRangeCalendar
      fromDate={fromDate}
      toDate={toDate}
      onChange={(from, to) => { setFromDate(from); setToDate(to); }}
      maxDate={maxDate}
      todayISO={todayISO}
    />
  );

  const quickSelectRow = quickSelects.length > 0 && (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: isMobile ? 8 : 6,
        marginTop: 12,
        paddingTop: 12,
        borderTop: `1px solid ${T.divider}`,
      }}
    >
      {quickSelects.map((opt) => {
        const active = activeToken === opt.token;
        return (
          <button
            key={opt.token}
            type="button"
            onClick={() => onSelectToken(opt.token)}
            style={{
              padding: isMobile ? '9px 14px' : '6px 12px',
              borderRadius: 8,
              border: `1px solid ${active ? T.accent : T.border}`,
              background: active ? T.accentSoft : 'transparent',
              color: active ? T.accent : T.t2,
              fontSize: isMobile ? 12.5 : 11.5,
              fontWeight: active ? 600 : 500,
              cursor: 'pointer',
              fontFamily: T.f,
              whiteSpace: 'nowrap',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );

  const actions = (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
      <button
        type="button"
        onClick={onClose}
        style={{
          padding: isMobile ? '10px 18px' : '6px 14px',
          borderRadius: 8,
          border: `1px solid ${T.border}`,
          background: 'transparent',
          fontSize: isMobile ? 13 : 11.5,
          fontWeight: 500,
          color: T.t2,
          cursor: 'pointer',
          fontFamily: T.f,
        }}
      >
        Cancel
      </button>
      <button
        type="button"
        disabled={rangeInvalid}
        onClick={rangeInvalid ? undefined : () => onApplyRange(customToken(fromDate, toDate))}
        style={{
          padding: isMobile ? '10px 22px' : '6px 16px',
          borderRadius: 8,
          border: 'none',
          background: rangeInvalid ? T.hover : T.accent,
          fontSize: isMobile ? 13 : 11.5,
          fontWeight: 600,
          color: rangeInvalid ? T.t3 : '#fff',
          cursor: rangeInvalid ? 'not-allowed' : 'pointer',
          fontFamily: T.f,
        }}
      >
        Apply
      </button>
    </div>
  );

  const body = (
    <>
      {calendar}
      {quickSelectRow}
      {actions}
    </>
  );

  if (isMobile) {
    const sheet = (
      <>
        <div
          onClick={onClose}
          style={{ position: 'fixed', inset: 0, background: 'rgba(17,19,28,0.45)', zIndex: 1200 }}
        />
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label="Select date range"
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            background: T.surface,
            borderTopLeftRadius: 18,
            borderTopRightRadius: 18,
            boxShadow: '0 -6px 28px rgba(0,0,0,0.18)',
            padding: '10px 18px calc(18px + env(safe-area-inset-bottom))',
            zIndex: 1201,
            fontFamily: T.f,
            maxHeight: '88vh',
            overflowY: 'auto',
          }}
        >
          {/* Drag affordance — the sheet is dismissed by the backdrop, Cancel or Escape. */}
          <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 12 }}>
            <div style={{ width: 38, height: 4, borderRadius: 2, background: T.divider }} />
          </div>
          {body}
        </div>
      </>
    );

    /**
     * The sheet MUST be portalled to <body>, and no z-index value can substitute for it.
     *
     * This component renders inside TopBar, whose mobile wrapper is `position: sticky` with
     * `z-index: 100` — which creates a STACKING CONTEXT. Every z-index inside that subtree is
     * resolved against its siblings *within* the context, so the whole TopBar tree competes
     * with the rest of the page as one layer at z-index 100. BottomNav is `position: fixed`
     * at z-index 1000 in the ROOT context, so it painted over the sheet and swallowed the
     * quick-select row and the Cancel/Apply buttons — even though the sheet asks for 1201.
     *
     * Portalling moves the sheet into the root stacking context, where 1200/1201 genuinely
     * outrank the nav's 1000. Do not "fix" a future overlap here by raising the number.
     */
    return typeof document !== 'undefined' ? createPortal(sheet, document.body) : sheet;
  }

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Select date range"
      style={{
        position: 'absolute',
        top: '100%',
        left: alignRight ? 'auto' : 0,
        right: alignRight ? 0 : 'auto',
        marginTop: 8,
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: 12,
        boxShadow: '0 8px 28px rgba(0,0,0,0.14)',
        padding: 16,
        zIndex: 300,
        width: DESKTOP_PANEL_WIDTH,
        fontFamily: T.f,
      }}
    >
      {body}
    </div>
  );
}
